import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ScreenPreview from './ScreenPreview';

const PROMPTS = [
  { text: 'Happy Hour starts at 4.', theme: 'happyHour' },
  { text: 'Now Hiring', theme: 'hiring' },
  { text: 'Fresh Coffee', theme: 'coffee' },
];

export default function TypingDemo({ className = '' }) {
  const [promptIndex, setPromptIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showGenerating, setShowGenerating] = useState(false);

  const currentPrompt = PROMPTS[promptIndex];

  useEffect(() => {
    const target = currentPrompt.text;
    let charIndex = 0;
    setDisplayText('');
    setIsTyping(true);
    setShowGenerating(false);

    const typeInterval = setInterval(() => {
      if (charIndex < target.length) {
        setDisplayText(target.slice(0, charIndex + 1));
        charIndex += 1;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
        setTimeout(() => setShowGenerating(true), 400);
        setTimeout(() => {
          setShowGenerating(false);
          setPromptIndex((i) => (i + 1) % PROMPTS.length);
        }, 3500);
      }
    }, 60);

    return () => clearInterval(typeInterval);
  }, [promptIndex, currentPrompt.text]);

  return (
    <div className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${className}`}>
      <div className="space-y-4">
        <div className="glass-card rounded-2xl p-1">
          <div className="bg-invert-navy-light/50 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-invert-orange animate-pulse" />
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                AI Composer
              </span>
            </div>
            <div className="min-h-[3rem] flex items-center">
              <span className="text-xl lg:text-2xl text-white font-medium">
                {displayText}
                {isTyping && (
                  <span className="inline-block w-0.5 h-6 bg-invert-orange ml-0.5 animate-blink align-middle" />
                )}
              </span>
            </div>
            {!isTyping && showGenerating && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 mt-4 text-sm text-invert-orange"
              >
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating design, animations, and layout...
              </motion.div>
            )}
          </div>
        </div>
        <p className="text-sm text-slate-400 pl-2">
          Try: &ldquo;Happy Hour starts at 4.&rdquo; · &ldquo;Now Hiring&rdquo; · &ldquo;Fresh Coffee&rdquo;
        </p>
      </div>

      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPrompt.theme}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <ScreenPreview theme={currentPrompt.theme} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
